import { withNewbornSubjects } from './newbornSubjects';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { OfflineIdentity } from './offline';
import type { ClockSample } from './offlineClock';
import { type OfflineBatch } from './offlineAdapter';
import { prepareOfflineShell } from './offlineShell';
import { getAccessToken } from '@/lib/auth/AuthContext';
import { bindOfflineIdleLock } from './offlineIdleLock';
import { downloadOfflineRecovery } from './offlineRecovery';
import type { ClinicalEpisode, ClinicalEvent, EventInput } from './types';
import { prepareEpisodes as prepareEpisodeBatch } from './offlinePreparation';
import { type OfflineStatus, OfflineVault, type PendingOperation } from './offline';
import {
  checkOfflineProvisioning,
  type EpisodeSnapshot,
  mayCreateLocalProposal,
  type OfflineEpisodeContext,
} from './offlineContext';
import {
  type PreparedResources,
  refreshPreparedResources,
  syncClock,
  type SyncHealth,
  synchronizePreparedEpisode,
} from './offlineSync';

const emptyStatus: OfflineStatus = {
  pending: 0,
  locked: true,
  syncing: false,
  signInRequired: 0,
  retryableErrors: 0,
  receiptMismatches: 0,
};
function mergeEvents(existing: ClinicalEvent[], incoming: ClinicalEvent[]) {
  return [...new Map([...existing, ...incoming].map((event) => [event.id, event])).values()].sort(
    (a, b) => a.sequence - b.sequence,
  );
}
const snapshotKey = (episodeId: string) => `episode:${episodeId}`;
export type OfflineFlowsheetController = ReturnType<typeof useOfflineFlowsheet>;
export function useOfflineFlowsheet({
  userId,
  episode,
  context,
  onlineEvents,
  organizationId,
}: {
  organizationId: string;
  userId: string;
  episode: ClinicalEpisode;
  onlineEvents?: ClinicalEvent[];
  context?: OfflineEpisodeContext;
}) {
  const vault = useRef<OfflineVault | null>(null);
  const generation = useRef(0);
  const identity = useRef<OfflineIdentity | null>(null);
  const syncing = useRef(false);
  const [unlocked, setUnlocked] = useState(false);
  const [cacheReady, setCacheReady] = useState(false);
  const [timerActions, setTimerActions] = useState<import('./BirthMonitoring').BirthTimerAction[]>([]);
  const [cachedEvents, setCachedEvents] = useState<ClinicalEvent[]>([]);
  const [pending, setPending] = useState<PendingOperation<OfflineBatch>[]>([]);
  const [status, setStatus] = useState<OfflineStatus>(emptyStatus);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [clock, setClock] = useState<ClockSample>();
  const [resourcesReady, setResourcesReady] = useState(false);
  const [health, setHealth] = useState<SyncHealth>();
  const [offlineSince, setOfflineSince] = useState<string | undefined>(() =>
    navigator.onLine ? undefined : new Date().toISOString(),
  );
  const episodeRef = useRef(episode);
  episodeRef.current = episode;
  const contextRef = useRef(context);
  contextRef.current = context;
  const onlineRef = useRef(onlineEvents);
  onlineRef.current = onlineEvents;
  const refresh = useCallback(
    async (current: OfflineVault) => {
      const snapshot = await current.getSnapshot<EpisodeSnapshot>(snapshotKey(episode.id));
      const operations = await current.listPending<OfflineBatch>();
      const state = await current.status();
      const resources = await current.getSnapshot<PreparedResources>('prepared-resources');
      const clock = await syncClock(current);
      const health = await current.getSnapshot<SyncHealth>('sync-health');
      if (vault.current !== current) return;
      setClock(clock);
      setResourcesReady(!!resources);
      setHealth(health);
      setCachedEvents(snapshot?.events ?? []);
      setTimerActions(snapshot?.timerActions ?? []);
      setCacheReady(!!snapshot && mayCreateLocalProposal(snapshot, organizationId, userId));
      setPending(operations.filter((operation) => operation.envelope.payload.episodeId === episode.id));
      setStatus(state);
    },
    [episode.id, organizationId, userId],
  );
  const cacheOnline = useCallback(
    async (current: OfflineVault, events: ClinicalEvent[]) => {
      await current.updateSnapshot<EpisodeSnapshot>(snapshotKey(episode.id), (previous) => ({
        ...previous,
        episode: episodeRef.current,
        cachedAt: new Date().toISOString(),
        context: contextRef.current ?? previous?.context,
        events: mergeEvents(previous?.events ?? [], events),
      }));
      await refresh(current);
    },
    [episode.id, refresh],
  );
  const lock = useCallback(() => {
    generation.current++;
    const current = vault.current;
    vault.current = null;
    current?.close();
    syncing.current = false;
    identity.current = null;
    setClock(undefined);
    setResourcesReady(false);
    setHealth(undefined);
    setBusy(false);
    setUnlocked(false);
    setCacheReady(false);
    setCachedEvents([]);
    setTimerActions([]);
    setPending([]);
    setStatus(emptyStatus);
    setNotice('Offline records are locked. Encrypted pending entries are retained.');
  }, []);
  useEffect(() => {
    setUnlocked(false);
    setCacheReady(false);
    setCachedEvents([]);
    setTimerActions([]);
    setPending([]);
    setStatus(emptyStatus);
    setBusy(false);
    return () => {
      generation.current++;
      vault.current?.close();
      vault.current = null;
    };
  }, [organizationId, userId, episode.id]);
  useEffect(() => {
    if (unlocked) return bindOfflineIdleLock(lock);
  }, [unlocked, lock]);
  const sync = useCallback(
    async (automatic = false) => {
      const current = vault.current;
      const actor = identity.current;
      if (!current || !actor || syncing.current) return;
      syncing.current = true;
      setBusy(true);
      setNotice('');
      try {
        await synchronizePreparedEpisode(current, actor, episode.id, getAccessToken(), { automatic });
        await refresh(current);
      } catch {
        if (vault.current === current) {
          setNotice('Sync could not finish. Saved entries are retained; check connection or account access.');
          await refresh(current).catch(() => undefined);
        }
      } finally {
        if (vault.current === current) {
          syncing.current = false;
          setBusy(false);
        }
      }
    },
    [episode.id, refresh],
  );
  const unlock = useCallback(
    async (passphrase: string) => {
      if (!organizationId || !userId) throw new Error('Sign in before enabling offline storage.');
      const sessionGeneration = generation.current;
      setBusy(true);
      setNotice('');
      let current: OfflineVault | undefined;
      try {
        const deviceKey = `flowsheets-offline-device:${organizationId}:${userId}`;
        let deviceId = localStorage.getItem(deviceKey);
        if (!deviceId) {
          deviceId = `offline_${crypto.randomUUID()}`;
          localStorage.setItem(deviceKey, deviceId);
        }
        current = await OfflineVault.open({ userId, deviceId, passphrase, organizationId });
        identity.current = { userId, deviceId, organizationId };
        if (sessionGeneration !== generation.current) throw new Error('Offline session changed.');
        vault.current?.close();
        vault.current = current;
        const previous = await current.getSnapshot<EpisodeSnapshot>(snapshotKey(episode.id));
        if (onlineRef.current && navigator.onLine) {
          try {
            const authorization = await checkOfflineProvisioning(organizationId, userId, getAccessToken());
            await current.putSnapshot(snapshotKey(episode.id), {
              ...previous,
              authorization,
              episode: episodeRef.current,
              context: contextRef.current,
              events: previous?.events ?? [],
              cachedAt: new Date().toISOString(),
            });
            await cacheOnline(current, onlineRef.current);
            await refreshPreparedResources(current, { userId, deviceId, organizationId }, getAccessToken());
            await prepareOfflineShell().catch(() =>
              setNotice(
                'Local records are ready, but offline page installation did not finish. Keep this page open and retry online.',
              ),
            );
            if (navigator.storage?.persist) await navigator.storage.persist().catch(() => false);
          } catch (error) {
            if (!previous?.authorization) throw error;
            setNotice(
              'Unlocked previously prepared records. Server refresh is unavailable; queued entries still require server authorization.',
            );
          }
        } else if (!previous?.authorization) throw new Error('Prepare this episode online first.');
        await refresh(current);
        if (sessionGeneration !== generation.current) throw new Error('Offline session changed.');
        setUnlocked(true);
      } catch {
        current?.close();
        if (vault.current === current) vault.current = null;
        if (sessionGeneration === generation.current) {
          setUnlocked(false);
          setCacheReady(false);
          setNotice(
            'Could not unlock or prepare this episode. Check the offline passphrase and load the episode online first.',
          );
        }
        throw new Error('Offline preparation did not complete.');
      } finally {
        if (sessionGeneration === generation.current) setBusy(false);
      }
    },
    [organizationId, userId, episode.id, cacheOnline, refresh],
  );
  useEffect(() => {
    const current = vault.current;
    if (unlocked && current && onlineEvents)
      void cacheOnline(current, onlineEvents).catch(() =>
        setNotice('Could not update the encrypted read cache. Existing saved entries are retained.'),
      );
  }, [unlocked, onlineEvents, cacheOnline]);
  useEffect(() => {
    if (!unlocked) return;
    const resume = () => {
      setOfflineSince(undefined);
      void sync();
    };
    const offline = () => setOfflineSince((value) => value ?? new Date().toISOString());
    const timer = window.setInterval(() => {
      if (navigator.onLine && !document.hidden) void sync(true);
    }, 5000);
    window.addEventListener('online', resume);
    window.addEventListener('offline', offline);
    if (navigator.onLine) void sync();
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('online', resume);
      window.removeEventListener('offline', offline);
    };
  }, [unlocked, sync]);
  const resolve = useCallback(
    async (id: string, action: 'retry' | 'hold', reason: string) => {
      const current = vault.current;
      if (!current) throw new Error('Unlock offline records first.');
      await current.resolveOperation(id, action, reason);
      await refresh(current);
      if (action === 'retry' && navigator.onLine) void sync();
    },
    [refresh, sync],
  );
  const save = useCallback(
    async (inputs: EventInput[]) => {
      const current = vault.current;
      if (!current || !cacheReady) throw new Error('Unlock the prepared episode before saving offline.');
      const prepared = await current.getSnapshot<EpisodeSnapshot>(snapshotKey(episode.id));
      if (!prepared || !mayCreateLocalProposal(prepared, organizationId, userId))
        throw new Error(
          'Offline authorization expired or is unavailable. Reconnect to renew; saved entries remain retained.',
        );
      const allowedEpisode = await withNewbornSubjects(episode);
      if (vault.current !== current) throw new Error('Workspace was locked.');
      if (
        episode.administration?.closed ||
        inputs.some(
          (input) =>
            !allowedEpisode.subjects.some((subject) => subject.id === input.subject_id && subject.active !== false),
        )
      )
        throw new Error('This episode or subject no longer accepts new observations.');
      const batch: OfflineBatch = {
        episodeId: episode.id,
        inputs: inputs.map(({ client_seq: _seq, device_id: _device, ...input }) => input),
      };
      if (!batch.inputs.length || batch.inputs.length > 50) throw new Error('Unsupported observation batch.');
      await current.enqueue({ payload: batch, id: crypto.randomUUID() });
      // Durable local commit means the form may clear. Rendering/sync failures must not invite duplicate entry.
      await refresh(current).catch(() => setNotice('Saved locally. Refresh the pending list after unlocking.'));
      if (navigator.onLine) void sync();
    },
    [cacheReady, episode, organizationId, userId, refresh, sync],
  );
  const prepareEpisodes = async (ids: string[]) => {
    const current = vault.current;
    const bound = identity.current;
    if (!current || !bound || !navigator.onLine) throw new Error('Unlock while connected before preparing episodes.');
    const result = await prepareEpisodeBatch(current, bound, ids, getAccessToken(), () => vault.current === current);
    if (vault.current === current) await refresh(current);
    return result;
  };
  const saveTimerAction = async (action: Omit<import('./BirthMonitoring').BirthTimerAction, 'author_id'>) => {
    const current = vault.current;
    if (!current || !cacheReady) throw new Error('Unlock the prepared workspace first.');
    await current.updateSnapshot<EpisodeSnapshot>(snapshotKey(episode.id), (previous) => {
      if (!previous || !mayCreateLocalProposal(previous, organizationId, userId))
        throw new Error('Renew local authorization.');
      return { ...previous, timerActions: [...(previous.timerActions ?? []), { ...action, author_id: userId }] };
    });
    await refresh(current);
  };
  const exportRecovery = async () => {
    if (!vault.current) throw new Error('Unlock first');
    await downloadOfflineRecovery(vault.current);
  };
  return {
    busy,
    lock,
    sync,
    save,
    clock,
    status,
    notice,
    health,
    unlock,
    pending,
    resolve,
    unlocked,
    cacheReady,
    cachedEvents,
    exportRecovery,
    timerActions,
    saveTimerAction,
    resourcesReady,
    prepareEpisodes,
    offlineSince: offlineSince ?? health?.offlineSince,
  };
}
